/**
 * Display formatting. Dates cross the wire as ISO-8601 strings (see
 * `isoDateTimeSchema` in `@wend/shared`) and are rendered in the viewer's own
 * locale and timezone.
 */

const DATE = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/**
 * Group separators for credit counts. A balance is read as a quantity and
 * `50000` is misread as `5000` at a glance far more often than `50,000` is —
 * which is the exact misreading a top-up screen must not invite.
 *
 * Indian grouping (`1,25,000`), fixed rather than the viewer's locale, since
 * slice 66. The credits panel now draws its balances with `@wend/ui`'s
 * `MetricPanel`, which formats in `en-IN` like every figure in the product,
 * and the review sentence under it ("goes from 1,25,000 to 1,30,000") uses
 * this function. With the viewer's locale, a browser set to en-US would have
 * put `1,25,000` in the panel and `125,000` in the sentence about the same
 * balance — two spellings of one number on the screen where a number typed
 * blind is the failure the panel exists to prevent.
 */
const COUNT = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const DAY_MS = 86_400_000;

/** Em dash for anything unparseable, so a bad value never renders as `Invalid Date`. */
const ABSENT = '—';

function parse(iso: string): Date | null {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : new Date(time);
}

export function formatDate(iso: string): string {
  const date = parse(iso);
  return date === null ? ABSENT : DATE.format(date);
}

export function formatDateTime(iso: string): string {
  const date = parse(iso);
  return date === null ? ABSENT : DATE_TIME.format(date);
}

/** `in 24 days` / `3 days ago` / `next month`. Empty string when unparseable. */
export function formatRelative(iso: string): string {
  const date = parse(iso);
  if (date === null) return '';

  const days = Math.round((date.getTime() - Date.now()) / DAY_MS);
  if (Math.abs(days) >= 60) return RELATIVE.format(Math.round(days / 30), 'month');
  return RELATIVE.format(days, 'day');
}

/** `1–25 of 42`, for a list footer. */
export function formatRange(page: number, pageSize: number, total: number): string {
  if (total === 0) return '0 of 0';
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `${first}–${last} of ${total}`;
}

/** A whole count — credits, rows — grouped in the viewer's locale. */
export function formatCount(value: number): string {
  return COUNT.format(value);
}
